import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Field } from '@navozyme/uikit/dist/atoms/Field';
import { DatePickerTimeInput } from '@navozyme/uikit/dist/molecules/DatePicker';
import { ColumnDef } from '@tanstack/react-table';
import { Form, Formik, FormikErrors } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { AppDispatch } from 'app/store';

import {
  selectSolas,
  ShipToShipOperation,
  updateIsps,
} from '../../../slices/portCallSlice';
import { IspsPortType } from '../../health/slices/healthSlice';

import Input from '../../../../forms/components/Input';
import MultiSelect from '../../../../forms/components/MultiSelect';
import { cleanEmptyData } from '../../../../global/utils/cleanEmptyData';
import {
  formatDateTimeInTable,
  isCrossedDates,
  isDateFromOrDateToOverlapWithAnyDate,
} from '../../../../global/utils/datetimes';
import { validateSchema } from '../../../../global/utils/validateForms';
import Button from '../../../../layout/components/Button';
import ConfirmationModal from '../../../../layout/components/ConfirmationModal';
import Modal from '../../../../layout/components/Modal';
import ConfirmDeleteModal from '../../../../modals/components/ConfirmDeleteModal';
import Table from '../../../../tables/components/Table';

import plusIcon from '../../../../tables/styles/images/plusIcon.svg';

import '../styles/solas-security-measures.scss';

const shipToShipColumns: ColumnDef<any>[] = [
  {
    id: 'dateFrom',
    header: 'Date from',
    accessorKey: 'dateFrom',
    cell: ({ getValue }) => formatDateTimeInTable({ value: getValue() }),
  },
  {
    id: 'dateTo',
    header: 'Date to',
    accessorKey: 'dateTo',
    cell: ({ getValue }) => formatDateTimeInTable({ value: getValue() }),
  },
  {
    id: 'location',
    header: 'Location or Latitude & Longitude',
    accessorKey: 'location',
  },
  {
    id: 'activity',
    header: 'Ship-to-ship activity',
    accessorKey: 'activity',
  },
  {
    id: 'securityMeasure',
    header: 'Security measure (only if SSP was not followed)',
    accessorKey: 'securityMeasure',
  },
];

type ShipToShipOperationValues = Omit<
  ShipToShipOperation,
  'dateFrom' | 'dateTo'
> & {
  dateFrom: Date | null;
  dateTo: Date | null;
  portcall: any;
};

const ShipToShipOperationsTable = () => {
  const dispatch = useDispatch<AppDispatch>();
  const params = useParams();
  const solas = useSelector(selectSolas);

  const [isDateFromDisabled, setIsDateFromDisabled] = useState(true);
  const [isDateToDisabled, setIsDateToDisabled] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lastPortCalls, setLastPortCalls] = useState<
    {
      label: string;
      value: IspsPortType;
    }[]
  >([]);
  const [refreshUuid, setRefreshUuid] = useState(uuidv4());

  useEffect(() => {
    if (solas?.data?.last10PortCalls) {
      setLastPortCalls(
        solas?.data?.last10PortCalls
          .filter(
            (portcall) => portcall?.arrivalDate && portcall?.departureDate
          )
          .map((portcall) => {
            return {
              label: `${portcall?.portName}, ${portcall?.portCountry}`,
              value: portcall,
            };
          })
      );
    }
  }, [solas?.data?.last10PortCalls]);

  let initialValues: ShipToShipOperationValues = {
    dateFrom: null,
    dateTo: null,
    location: '',
    activity: '',
    securityMeasure: '',
    portcall: null,
  };

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [formValues, setFormValues] =
    useState<ShipToShipOperationValues | null>(null);

  const validationSchema = Yup.object({
    dateFrom: Yup.string().required('*This field is required'),
    dateTo: Yup.string().required('*Please select one'),
    activity: Yup.string().required('*This field is required'),
    location: Yup.string().required('*This field is required'),
    securityMeasure: Yup.string().required('*This field is required'),
  });

  const handleSubmit = async (values: ShipToShipOperationValues) => {
    const finalValues: ShipToShipOperation & {
      portcall?: IspsPortType;
    } = {
      ...values,
      dateFrom: values.dateFrom ? values.dateFrom.toISOString() : '',
      dateTo: values.dateTo ? values.dateTo.toISOString() : '',
      portcall: undefined,
    };

    cleanEmptyData(finalValues);
    if (params.uid) {
      if (isEditingRow) {
        const newMembers = solas.data.shipToShipOperations.map((member, i) => {
          if (i === editingRow) {
            return finalValues;
          }
          return member;
        });

        dispatch(
          updateIsps({
            ispsData: {
              shipToShipOperations: newMembers,
            },
            uid: params.uid,
          })
        );
        setEditingRow(null);
      } else {
        let shipToShipOperations = [];
        if (solas.data.shipToShipOperations) {
          shipToShipOperations =
            solas.data.shipToShipOperations.concat(finalValues);
        } else {
          shipToShipOperations.push(finalValues);
        }
        dispatch(
          updateIsps({
            ispsData: {
              shipToShipOperations: shipToShipOperations,
            },
            uid: params.uid,
          })
        );
      }
    }
    setDateFrom(null);
    setDateTo(null);
    setIsModalOpen(false);
  };

  const [isEditingTable, setIsEditingTable] = useState<boolean>(false);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const isEditingRow = editingRow !== null;

  const row: ShipToShipOperation | null = isEditingRow
    ? solas.data.shipToShipOperations[editingRow]
    : null;
  if (row) {
    initialValues = {
      dateFrom: row.dateFrom ? new Date(row.dateFrom) : null,
      dateTo: row.dateTo ? new Date(row.dateTo) : null,
      location: row.location ? row.location : '',
      activity: row.activity ? row.activity : '',
      securityMeasure: row.securityMeasure ? row.securityMeasure : '',
      portcall: lastPortCalls.find((portcall) =>
        isDateFromOrDateToOverlapWithAnyDate(
          new Date(row?.dateFrom ?? ''),
          new Date(portcall?.value?.arrivalDate ?? ''),
          new Date(portcall?.value?.departureDate ?? '')
        )
      )?.value,
    };
  }

  const [dateFrom, setDateFrom] = useState<Date | null>(
    initialValues?.dateFrom ? new Date(initialValues?.dateFrom) : null
  );
  const [dateTo, setDateTo] = useState<Date | null>(
    initialValues?.dateTo ? new Date(initialValues?.dateTo) : null
  );

  const [confirmDeleteMemberModalData, setConfirmDeleteMemberModalData] =
    useState<{
      index: number | null;
      showModal: boolean;
    }>({
      index: null,
      showModal: false,
    });
  const [
    showConfirmDeleteAllMembersModal,
    setShowConfirmDeleteAllMembersModal,
  ] = useState(false);

  const validateDates = (
    values: ShipToShipOperationValues,
    setValues: (
      values: React.SetStateAction<ShipToShipOperationValues>,
      shouldValidate?: boolean
    ) => Promise<void | FormikErrors<ShipToShipOperationValues>>
  ) => {
    if (
      values?.dateFrom &&
      values?.dateTo &&
      isCrossedDates(new Date(values?.dateFrom), new Date(values?.dateTo))
    ) {
      setValues({
        ...values,
        dateFrom: new Date(values?.portcall?.arrivalDate + ':00Z'),
        dateTo: new Date(values?.portcall?.departureDate + ':00Z'),
      });
    } else if (
      values?.dateFrom &&
      !isDateFromOrDateToOverlapWithAnyDate(
        new Date(values?.dateFrom),
        new Date(values?.portcall?.arrivalDate),
        new Date(values?.portcall?.departureDate)
      )
    ) {
      setValues({
        ...values,
        dateFrom: new Date(values?.portcall?.arrivalDate + ':00Z'),
        dateTo: new Date(values?.portcall?.departureDate + ':00Z'),
      });
    } else {
      setValues({
        ...values,
        dateFrom: values?.dateFrom,
        dateTo: values?.dateTo,
      });
    }
    setRefreshUuid(uuidv4());
  };

  return (
    <div className="solas-port-calls-table">
      <Modal
        className={`add-ship-to-ship-modal`}
        title={'Add a ship-to-ship operation'}
        showModal={isModalOpen}
        onAfterOpen={() => {
          setIsDateFromDisabled(true);
          setIsDateToDisabled(true);
        }}
        onCloseModal={() => {
          setDateFrom(null);
          setDateTo(null);
          setIsModalOpen(false);
          if (isEditingRow) {
            setTimeout(() => setEditingRow(null), 200);
          }
        }}>
        <ConfirmationModal
          showModal={isConfirmationModalOpen}
          handleCloseModal={setIsConfirmationModalOpen}
          willDisableSave={formValues}
          onAcceptModal={() => {
            if (formValues) {
              setIsConfirmationModalOpen(false);
              setIsModalOpen(false);
              setEditingRow(null);
              handleSubmit(formValues);
            }
          }}
          className=""
        />
        <div className="add-ship-to-ship-modal__content">
          <Formik
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
            validate={(values) => {
              return validateSchema(
                values,
                validationSchema,
                setFormValues,
                setIsConfirmationModalOpen,
                setIsModalOpen
              );
            }}>
            {({ values, setValues, errors }) => {
              return (
                <Form className="add-ship-to-ship-form">
                  <div className="add-ship-to-ship-form__row">
                    <MultiSelect
                      key={
                        lastPortCalls?.length +
                        values?.portcall?.departureDate +
                        values?.portcall?.arrivalDate +
                        'portcall'
                      }
                      name="portcall"
                      options={lastPortCalls}
                      isMulti={false}
                      label="Select port call"
                      placeholder="Choose one"
                      onChange={(value: { value: IspsPortType }) => {
                        if (
                          value?.value?.arrivalDate &&
                          value?.value?.departureDate
                        ) {
                          setValues({
                            ...values,
                            dateFrom: new Date(value?.value?.arrivalDate),
                            dateTo: new Date(value?.value?.departureDate),
                          });
                          setDateFrom(new Date(value?.value?.arrivalDate));
                          setDateTo(new Date(value?.value?.departureDate));
                          setIsDateFromDisabled(false);
                          setIsDateToDisabled(false);
                        } else {
                          setIsDateFromDisabled(true);
                          setIsDateToDisabled(true);
                        }
                      }}
                    />
                    <Field label="Date From" className="field_date_picker">
                      <DatePickerTimeInput
                        key={
                          initialValues?.dateFrom +
                          values?.portcall?.arrivalDate +
                          'dateFrom' +
                          refreshUuid
                        }
                        name="date"
                        sizeInput="large"
                        disabled={isDateFromDisabled}
                        maxDate={new Date(values?.portcall?.departureDate)}
                        minDate={new Date(values?.portcall?.arrivalDate)}
                        showTime={true}
                        defaultValue={dateFrom}
                        action={(selected: {
                          inputDate: string;
                          inputTime: string;
                        }) => {
                          if (selected.inputDate && selected.inputTime) {
                            const isoDate = `${selected.inputDate}T${selected.inputTime}`;
                            setDateFrom(new Date(isoDate));
                            validateDates(
                              {
                                ...values,
                                dateFrom: new Date(isoDate + ':00Z'),
                                dateTo: values?.dateTo,
                              },
                              setValues
                            );
                          }
                        }}
                        hasError={errors?.dateFrom}
                        errorMessage={errors?.dateFrom}
                        popperPlacement="right"
                      />
                    </Field>
                    <Field label="Date To" className="field_date_picker">
                      <DatePickerTimeInput
                        key={
                          initialValues?.dateTo +
                          values?.portcall?.departureDate +
                          'dateTo' +
                          refreshUuid
                        }
                        name="date"
                        sizeInput="large"
                        disabled={isDateToDisabled}
                        maxDate={new Date(values?.portcall?.departureDate)}
                        minDate={new Date(values?.portcall?.arrivalDate)}
                        showTime={true}
                        defaultValue={dateTo}
                        action={(selected: {
                          inputDate: string;
                          inputTime: string;
                        }) => {
                          if (selected.inputDate && selected.inputTime) {
                            const isoDate = `${selected.inputDate}T${selected.inputTime}`;
                            setDateTo(new Date(isoDate));
                            validateDates(
                              {
                                ...values,
                                dateTo: new Date(isoDate + ':00Z'),
                                dateFrom: values?.dateFrom,
                              },
                              setValues
                            );
                          }
                        }}
                        hasError={errors?.dateTo}
                        errorMessage={errors?.dateTo}
                        popperPlacement="right"
                      />
                    </Field>
                  </div>
                  <div className="add-ship-to-ship-form__row">
                    <Input
                      name="location"
                      placeholder="Type one"
                      label="Location or Latitude & Longitude"
                      disabled={!values?.portcall}
                    />
                    <Input
                      name="activity"
                      placeholder="Type one"
                      label="Ship-to-ship activity"
                    />
                  </div>
                  <div className="add-ship-to-ship-form__row">
                    <Input
                      name="securityMeasure"
                      placeholder="Type one"
                      label="Security measure (only if SSP was not followed)"
                    />
                  </div>
                  {isEditingRow ? (
                    <div className="add-ship-to-ship__form-submission-btns">
                      <div
                        className="discard-btn"
                        onClick={() => {
                          setIsModalOpen(false);
                          setTimeout(() => setEditingRow(null), 300);
                        }}>
                        Discard
                      </div>
                      <Button type="submit">Change Information</Button>
                    </div>
                  ) : (
                    <div className="add-ship-to-ship__form-submission-btns">
                      <Button iconLeft={plusIcon} type="submit">
                        Add
                      </Button>
                    </div>
                  )}
                </Form>
              );
            }}
          </Formik>
        </div>
      </Modal>
      <ConfirmDeleteModal
        title={
          <h3>
            Are you sure you want to{' '}
            <span>
              delete all <br />
              ship-to-ship operations
            </span>{' '}
            from the list?
          </h3>
        }
        show={showConfirmDeleteAllMembersModal}
        onCancelClick={() => setShowConfirmDeleteAllMembersModal(false)}
        onDeleteClick={() => {
          if (params.uid) {
            dispatch(
              updateIsps({
                ispsData: {
                  shipToShipOperations: [],
                },
                uid: params.uid,
              })
            );
            setShowConfirmDeleteAllMembersModal(false);
          }
        }}
        onCloseModal={() => setShowConfirmDeleteAllMembersModal(false)}
      />
      <ConfirmDeleteModal
        title={
          <h3>Are you sure you want to delete this ship-to-ship operation?</h3>
        }
        show={confirmDeleteMemberModalData.showModal}
        onCancelClick={() => {
          setConfirmDeleteMemberModalData({
            index: null,
            showModal: false,
          });
        }}
        onDeleteClick={() => {
          const shipToShipOperations = [...solas.data.shipToShipOperations];
          confirmDeleteMemberModalData.index &&
            shipToShipOperations.splice(confirmDeleteMemberModalData.index, 1);

          if (params.uid) {
            dispatch(
              updateIsps({
                ispsData: {
                  shipToShipOperations,
                },
                uid: params.uid,
              })
            );

            setConfirmDeleteMemberModalData({
              index: null,
              showModal: false,
            });
          }
        }}
        onCloseModal={() => setShowConfirmDeleteAllMembersModal(false)}
      />
      <h4>
        Please indicate the ship-to-ship operations carried out during the last
        10 port calls.{' '}
      </h4>
      <p>
        In case the SSP procedures were not followed, please indicate it in the
        table.
      </p>
      <Table
        useRowNumbers
        title={'Ship-to-ship operations'}
        addButtonText={'Add ship-to-ship operation'}
        data={solas.data.shipToShipOperations || []}
        columns={shipToShipColumns}
        editingMode={isEditingTable}
        setEditingMode={setIsEditingTable}
        setIsAddModalOpen={setIsModalOpen}
        setIsDeleteModalOpen={setShowConfirmDeleteAllMembersModal}
        onEditButtonClick={(rowIndex) => {
          setEditingRow(rowIndex);
          setIsModalOpen(true);
        }}
        onDeleteButtonClick={(rowIndex) => {
          setConfirmDeleteMemberModalData({
            showModal: true,
            index: rowIndex,
          });
        }}
      />
    </div>
  );
};

export default ShipToShipOperationsTable;
